import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { AutoComplete, Input, Select, Tooltip } from 'antd';
import { ModalComponent } from 'src/components/baseComponents/Modal';
import { useGetEhrConfigs } from 'src/hooks/ehr/useGetEhrConfigs';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { useGetSummaryList } from 'src/hooks/summary';
import { debounce } from 'lodash';
import { ambientApi } from 'src/services';
import { Field, useFormikContext } from 'formik';
import { getLatestSummary } from 'src/utils/summary/helpers';

const MedicineSearchField = ({ value, onChange, onSelectMedicine }) => {
  const { data: practitioner } = useGetCurrentPractitioner();
  const [medicineSearchQuery, setMedicineSearchQuery] = useState('');
  const [medicineSearchResults, setMedicineSearchResults] = useState([]);
  const [isMedicineSearching, setIsMedicineSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const fetchMedicineResults = async (searchText, pageNum) => {
    if (!searchText || searchText.length < 3) {
      setMedicineSearchResults([]);
      setHasMore(false);
      return;
    }

    setIsMedicineSearching(true);
    try {
      const response = await ambientApi.get('/medicines', {
        params: {
          search_string: searchText,
          org_uuid: practitioner?.org_uuid,
          page: pageNum,
        },
      });

      if (response.data && Array.isArray(response.data)) {
        const formattedResults = response.data.map((medicine) => ({
          key: medicine.order_type_id || medicine.display_name,
          value: medicine.display_name,
          label: medicine.display_name,
          order_type_id: medicine.order_type_id,
          display_name: medicine.display_name,
        }));

        if (pageNum === 1) {
          setMedicineSearchResults(formattedResults);
        } else {
          setMedicineSearchResults((prev) => [...prev, ...formattedResults]);
        }
        if (response.data.length < 20) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
        setPage(pageNum);
      } else {
        if (pageNum === 1) {
          setMedicineSearchResults([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      setMedicineSearchResults([]);
      setHasMore(false);
    } finally {
      setIsMedicineSearching(false);
    }
  };

  const debouncedMedicineSearch = useCallback(
    debounce((searchText) => {
      setMedicineSearchQuery(searchText);
      fetchMedicineResults(searchText, 1);
    }, 300),
    [practitioner?.org_uuid]
  );

  const handleMedicineSelect = (selectedValue, option) => {
    onSelectMedicine(option);
  };

  const autocompleteOptions = useMemo(() => {
    const options = [];

    if (medicineSearchResults.length > 0) {
      options.push({
        label: 'Search Results',
        options: medicineSearchResults,
      });
    }

    return options;
  }, [medicineSearchResults]);

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop - clientHeight < 10 &&
      !isMedicineSearching &&
      hasMore
    ) {
      fetchMedicineResults(medicineSearchQuery, page + 1);
    }
  };

  return (
    <AutoComplete
      options={autocompleteOptions}
      onSelect={handleMedicineSelect}
      onSearch={debouncedMedicineSearch}
      placeholder="Search medicine (min. 3 characters)"
      value={value}
      onChange={onChange}
      className="w-full"
      size="large"
      notFoundContent={
        isMedicineSearching
          ? 'Searching...'
          : medicineSearchQuery.length >= 3
            ? 'No results found'
            : 'Type at least 3 characters to search'
      }
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
      onPopupScroll={handlePopupScroll}
    />
  );
};

const PrescriptionFormModal = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  encounterId,
}) => {
  const isEditMode = !!initialValues;
  const { data: ehrConfigs } = useGetEhrConfigs();

  // Fetch all SNOMED summaries for the encounter
  const { data: snomedData } = useGetSummaryList({
    encounterId,
    encounterPhase: 'in-visit',
    type: 'snomed',
  });

  // Memoize and parse the latest SNOMED summary
  const snomedCodes = useMemo(() => {
    if (!snomedData?.summaries) return [];

    const latestSummary = getLatestSummary(snomedData.summaries);
    if (!latestSummary) return [];

    try {
      return JSON.parse(latestSummary.summary_json || '[]');
    } catch (error) {
      console.error('Error parsing SNOMED summary_json:', error);
      return [];
    }
  }, [snomedData]);

  const dosageOptions =
    ehrConfigs?.dosage_quantity_units?.map((unit) => ({
      label: unit,
      value: unit,
    })) || [];

  const frequencyOptions =
    ehrConfigs?.dosage_frequencies?.map((freq) => ({
      label: freq,
      value: freq,
    })) || [];

  // Memoized and sorted ICD-10 code options for dropdowns
  const icd10Options = useMemo(() => {
    if (!snomedCodes) return [];

    // Get unique ICD10 codes with their descriptions
    const uniqueIcd10 = snomedCodes.reduce((acc, item) => {
      if (
        item.icd10_code &&
        !acc.find((existing) => existing.value === item.icd10_code)
      ) {
        acc.push({
          label: `${item.icd10_code} - ${item.description}`,
          value: item.icd10_code,
          description: item.description,
          snomed_code: item.code,
        });
      }
      return acc;
    }, []);

    return uniqueIcd10.sort((a, b) => a.value.localeCompare(b.value));
  }, [snomedCodes]);

  // Memoized and sorted SNOMED description options for dropdowns
  const snomedDescriptionOptions = useMemo(() => {
    if (!snomedCodes) return [];

    return snomedCodes
      .map((item, index) => ({
        label: `${item.description} (${item.icd10_code})`,
        value: item.description,
        icd10_code: item.icd10_code,
        code: item.code,
        confidence: item.confidence,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [snomedCodes]);

  const CustomFormBody = () => {
    const { values, setFieldValue, errors, touched } = useFormikContext();
    const justSelected = useRef(false);

    return (
      <div className="space-y-2">
        {/* Medicine Search Field - Full Width */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Medication Name
          </label>
          <div className="mb-4">
            <MedicineSearchField
              value={values.name}
              onChange={(name) => {
                if (justSelected.current) {
                  justSelected.current = false;
                } else {
                  // Manual typing, so clear order_type_id
                  setFieldValue('order_type_id', null);
                }
                setFieldValue('name', name);
              }}
              onSelectMedicine={(option) => {
                justSelected.current = true;
                setFieldValue('order_type_id', option.order_type_id);
                if (option.dosage_unit) {
                  setFieldValue('dosage_unit', option.dosage_unit);
                }
                // AutoComplete's onChange will set the name value
              }}
            />
          </div>
          {touched.name && errors.name && (
            <div className="text-red-500 text-xs mt-1">{errors.name}</div>
          )}
        </div>

        {/* Dosage and Dosage Unit - Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dosage
            </label>
            <Field name="dosage">
              {() => (
                <Input
                  placeholder="e.g., 500mg"
                  value={values.dosage}
                  onChange={(e) => setFieldValue('dosage', e.target.value)}
                  className="w-full"
                  size="large"
                />
              )}
            </Field>
            {touched.dosage && errors.dosage && (
              <div className="text-red-500 text-xs mt-1">{errors.dosage}</div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dosage Unit
            </label>
            <Field name="dosage_unit">
              {() => (
                <Select
                  placeholder="Select unit"
                  value={values.dosage_unit}
                  onChange={(value) => setFieldValue('dosage_unit', value)}
                  className="w-full"
                  options={dosageOptions}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            </Field>
            {touched.dosage_unit && errors.dosage_unit && (
              <div className="text-red-500 text-xs mt-1">
                {errors.dosage_unit}
              </div>
            )}
          </div>
        </div>

        {/* Frequency and Duration - Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequency
            </label>
            <Field name="frequency">
              {() => (
                <Select
                  placeholder="Select frequency"
                  value={values.frequency}
                  onChange={(value) => setFieldValue('frequency', value)}
                  className="w-full"
                  options={frequencyOptions}
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            </Field>
            {touched.frequency && errors.frequency && (
              <div className="text-red-500 text-xs mt-1">
                {errors.frequency}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration
            </label>
            <Field name="duration">
              {() => (
                <Input
                  placeholder="e.g., 10 days"
                  value={values.duration}
                  onChange={(e) => setFieldValue('duration', e.target.value)}
                  className="w-full"
                  size="large"
                />
              )}
            </Field>
            {touched.duration && errors.duration && (
              <div className="text-red-500 text-xs mt-1">{errors.duration}</div>
            )}
          </div>
        </div>

        {/* SNOMED Description and ICD10 Code - Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Associated Diagnosis (ICD-10)
            </label>
            <Field name="associated_diagnosis_code">
              {() => (
                <Select
                  placeholder={
                    icd10Options.length > 0
                      ? 'Select ICD10 code from encounter'
                      : 'Enter ICD10 code manually'
                  }
                  value={values.associated_diagnosis_code}
                  onChange={(value) => {
                    setFieldValue('associated_diagnosis_code', value);
                    setFieldValue(
                      'associated_diagnosis_description',
                      icd10Options.find((option) => option.value === value)
                        ?.description || ''
                    );
                    setFieldValue(
                      'snomed_code_description',
                      icd10Options.find((option) => option.value === value)
                        ?.description || ''
                    );
                    setFieldValue(
                      'snomed_code',
                      icd10Options.find((option) => option.value === value)
                        ?.snomed_code || ''
                    );
                  }}
                  className="w-full"
                  options={icd10Options}
                  size="large"
                  showSearch
                  allowClear
                  mode={icd10Options.length === 0 ? 'tags' : undefined}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            </Field>
            {touched.associated_diagnosis_code &&
              errors.associated_diagnosis_code && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.associated_diagnosis_code}
                </div>
              )}
          </div>
        </div>

        {/* Warnings - Full Width */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Condition (Evidence)
          </label>
          <Field name="condition">
            {() => (
              <Input.TextArea
                placeholder="Enter condition or evidence"
                value={values.condition}
                onChange={(e) => setFieldValue('condition', e.target.value)}
                rows={3}
              />
            )}
          </Field>
          {touched.condition && errors.condition && (
            <div className="text-red-500 text-xs mt-1">{errors.condition}</div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Warnings
          </label>
          <Field name="warnings">
            {() => (
              <Input.TextArea
                placeholder="e.g., May cause drowsiness"
                value={values.warnings}
                onChange={(e) => setFieldValue('warnings', e.target.value)}
                rows={3}
              />
            )}
          </Field>
          {touched.warnings && errors.warnings && (
            <div className="text-red-500 text-xs mt-1">{errors.warnings}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <ModalComponent
      open={open}
      handleCancel={onClose}
      handleSubmit={onSubmit}
      submitButtonText={isEditMode ? 'Update Prescription' : 'Add Prescription'}
      cancelButtonText="Cancel"
      customBody={<CustomFormBody />}
      sections={[
        {
          heading: isEditMode ? 'Edit Prescription' : 'Add New Prescription',
          fields: [],
        },
      ]}
      initialValues={
        initialValues || {
          name: '',
          dosage: '',
          dosage_unit: '',
          frequency: '',
          duration: '',
          condition: '',
          associated_diagnosis_code: '',
          snomed_code: '',
          snomed_code_description: '',
          warnings: '',
          order_type_id: '',
        }
      }
    />
  );
};

export default PrescriptionFormModal;
